"use client";

import { useState, useEffect, useCallback } from "react";
import type { ApiMigration } from "../types/migrations";
// Import the service's type directly
import type { GetMigrationsResponse as ServiceGetMigrationsResponse } from "../services/migration/types";
import { getAllMigrations } from "../services/migration/migrationList";
import { extractApiErrorMessage } from "../utils/error-handling";

interface UseMigrationsProps {
  accountId: string;
  autoFetch?: boolean;
}

interface UseMigrationsReturn {
  migrations: ApiMigration[];
  loading: boolean;
  error: string | null;
  fetchMigrations: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching and managing migrations
 */
export function useMigrations({ accountId, autoFetch = true }: UseMigrationsProps): UseMigrationsReturn {
  const [migrations, setMigrations] = useState<ApiMigration[]>([]);
  const [loading, setLoading] = useState<boolean>(autoFetch);
  const [error, setError] = useState<string | null>(null);

  const fetchMigrations = useCallback(async () => {
    if (!accountId) {
      setError("Valid account ID required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response: ServiceGetMigrationsResponse = await getAllMigrations();

      if (response.success && response.content) {
        // Transform the response to match our ApiMigration type if needed
        const transformedMigrations: ApiMigration[] = response.content.map((migration) => ({
          ...migration,
          id: migration.migrationId,
          // Add the status property to lastMigrationExecution
          lastMigrationExecution: {
            ...migration.lastMigrationExecution,
            status: "UNKNOWN", // Default value since it doesn't exist in the service type
          },
        }));

        setMigrations(transformedMigrations);
      } else {
        setError(extractApiErrorMessage(response.error) || "Error fetching migrations");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  // Load migrations automatically if autoFetch is true
  useEffect(() => {
    if (autoFetch && accountId) {
      fetchMigrations();
    }
  }, [autoFetch, accountId, fetchMigrations]);

  // Function to reload data
  const refetch = useCallback(() => {
    return fetchMigrations();
  }, [fetchMigrations]);

  return {
    migrations,
    loading,
    error,
    fetchMigrations,
    refetch,
  };
}
